import { Reaction } from '@/types/models';
import { createSelector } from '@reduxjs/toolkit';
import { commentReactionSelector } from '@/redux/comment/comment.selectors';
import { SIZE_OF_EMPTY_ARRAY } from '@/constants/common';
import { bioEntitySearchSelector } from '@/redux/bioEntity/bioEntitySearch.selectors';
import {
  currentDrawerReactionIdSelector,
  currentSelectedSearchElement,
} from '../drawer/drawer.selectors';
import { currentModelIdSelector, modelsDataSelector } from '../models/models.selectors';
import { rootSelector } from '../root/root.selectors';
import { getReferencesWithoutEmptyLink } from './utils/getFilteredReferences';
import { getReferencesGroupedBySource } from './utils/getGroupedReferences';

export const reactionsSelector = createSelector(rootSelector, state => state.reactions);

export const reactionsDataSelector = createSelector(
  reactionsSelector,
  reactions => reactions?.data || [],
);

export const allReactionsSelectorOfCurrentMap = createSelector(
  reactionsDataSelector,
  currentModelIdSelector,
  (reactions, currentModelId): Reaction[] => {
    return reactions.filter(({ modelId }) => modelId === currentModelId);
  },
);

export const currentDrawerReactionSelector = createSelector(
  reactionsDataSelector,
  commentReactionSelector,
  currentDrawerReactionIdSelector,
  (reactions, commentReaction, currentDrawerReactionId) => {
    if (commentReaction && commentReaction.id === currentDrawerReactionId) {
      return commentReaction;
    }

    return reactions.find(({ id }) => id === currentDrawerReactionId);
  },
);

export const currentDrawerReactionGroupedReferencesSelector = createSelector(
  currentDrawerReactionSelector,
  reaction => {
    const referencesFiltered = getReferencesWithoutEmptyLink(reaction);
    return getReferencesGroupedBySource(referencesFiltered);
  },
);

export const reactionIdsForSelectedSearchElementSelector = createSelector(
  bioEntitySearchSelector,
  currentSelectedSearchElement,
  (bioEntitiesState, currentSearchElement): number[] => {
    const results = bioEntitiesState.data.find(
      ({ searchQueryElement }) => searchQueryElement === currentSearchElement,
    );
    if (results) {
      const reactionsSearchData =
        results?.data?.filter(bioEntity => {
          return bioEntity.bioEntity.idReaction !== undefined;
        }) || [];
      const ids = reactionsSearchData.map(entry => Number(entry.bioEntity.id));
      return ids;
    }
    return [];
  },
);

export const reactionsPerModelSelector = createSelector(
  reactionsDataSelector,
  modelsDataSelector,
  reactionIdsForSelectedSearchElementSelector,
  (reactions, models, reactionIdsForSelectedSearchElement) => {
    const reactionsPerModelPerSearchElement = (models || []).map(model => {
      const reactionsInGivenModel = reactions.filter(
        entity =>
          model.idObject === entity.modelId &&
          reactionIdsForSelectedSearchElement.includes(entity.id),
      );

      return {
        modelName: model.name,
        modelId: model.idObject,
        numberOfEntities: reactionsInGivenModel.length,
        reactions: reactionsInGivenModel,
      };
    });

    return reactionsPerModelPerSearchElement.filter(
      model => model.numberOfEntities !== SIZE_OF_EMPTY_ARRAY,
    );
  },
);
